import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { usePosOrder } from '../hooks/usePosOrder';
import { usePosCart } from '../hooks/usePosCart';
import { formatCurrency } from '../utils/posCalculations';
import { PosService } from '../services/posService';
import type { EmitirOrdenRequest, TalonarioOption } from '../types/pos.types';
import { initialEmitirOrden } from '../types/pos.types';

export const PaymentDialog: React.FC = () => {
  const { paymentDialogOpen, closePayment, handleEmitir, saving, ordenActual, error: storeError } = usePosOrder();
  const { total } = usePosCart();
  const [formData, setFormData] = useState<EmitirOrdenRequest>(initialEmitirOrden);
  const [montoRecibido, setMontoRecibido] = useState<number>(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [talonarios, setTalonarios] = useState<TalonarioOption[]>([]);
  const [proximoNumero, setProximoNumero] = useState<number | null>(null);

  const error = localError || storeError;

  const cambio = montoRecibido - total;

  useEffect(() => {
    if (formData.idComprobante > 0) {
      PosService.getTalonarios(formData.idComprobante)
        .then((data) => {
          setTalonarios(data);
          if (data.length === 1) {
            setFormData(prev => ({
              ...prev,
              serie: data[0].serie,
              idTalonario: data[0].idTalonario,
            }));
            setProximoNumero(data[0].proximoNumero);
          } else {
            setFormData(prev => ({ ...prev, serie: '', idTalonario: undefined }));
            setProximoNumero(null);
          }
        })
        .catch(() => setTalonarios([]));
    } else {
      setTalonarios([]);
      setProximoNumero(null);
      setFormData(prev => ({ ...prev, serie: '', idTalonario: undefined }));
    }
  }, [formData.idComprobante]);

  const handleChange = (field: keyof EmitirOrdenRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTalonarioChange = (idTalonario: number) => {
    const selected = talonarios.find(t => t.idTalonario === idTalonario);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        serie: selected.serie,
        idTalonario: selected.idTalonario,
      }));
      setProximoNumero(selected.proximoNumero);
    }
  };

  const handleSubmit = async () => {
    if (!ordenActual) return;

    if (formData.idComprobante === 0) {
      setLocalError('Seleccione un tipo de comprobante');
      return;
    }
    if (!formData.idTalonario) {
      setLocalError('Seleccione una serie');
      return;
    }
    if (montoRecibido < total) {
      setLocalError('El monto recibido es menor al total');
      return;
    }

    setLocalError(null);
    try {
      await handleEmitir({
        ...formData,
        idOrdenPos: ordenActual.idOrdenPos,
      });
      setFormData(initialEmitirOrden);
      setMontoRecibido(0);
      setTalonarios([]);
      setProximoNumero(null);
    } catch {
      // Error is already formatted and set in the store by posStore.emitirOrden
    }
  };

  return (
    <Dialog open={paymentDialogOpen} onClose={closePayment} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          Cobrar Venta
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setLocalError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo Comprobante</InputLabel>
            <Select
              value={formData.idComprobante}
              label="Tipo Comprobante"
              onChange={(e) => handleChange('idComprobante', e.target.value)}
            >
              <MenuItem value={0}>Seleccionar...</MenuItem>
              <MenuItem value={29}>Boleta de Venta</MenuItem>
              <MenuItem value={12}>Factura</MenuItem>
              <MenuItem value={30}>Nota de Venta</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Serie</InputLabel>
              <Select
                value={formData.idTalonario || ''}
                label="Serie"
                onChange={(e) => handleTalonarioChange(e.target.value as number)}
                disabled={talonarios.length === 0}
              >
                {talonarios.map((t) => (
                  <MenuItem key={t.idTalonario} value={t.idTalonario}>
                    {t.serie}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Nro. Documento"
              size="small"
              value={proximoNumero ?? ''}
              disabled
              sx={{ width: 140 }}
            />
          </Box>

          <TextField
            label="Observaciones"
            size="small"
            value={formData.observaciones || ''}
            onChange={(e) => handleChange('observaciones', e.target.value)}
            multiline
            rows={2}
          />

          <Divider />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h5" fontWeight="bold">TOTAL:</Typography>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {formatCurrency(total)}
            </Typography>
          </Box>

          <TextField
            label="Monto Recibido"
            type="number"
            size="small"
            value={montoRecibido || ''}
            onChange={(e) => setMontoRecibido(Number(e.target.value))}
            slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
          />

          {montoRecibido > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 1.5,
                bgcolor: cambio >= 0 ? 'success.light' : 'error.light',
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" fontWeight="bold">Cambio:</Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(Math.max(0, cambio))}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={closePayment} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={<PaymentIcon />}
          size="large"
        >
          {saving ? 'Emitiendo...' : 'Emitir Comprobante'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
